import isBefore from 'date-fns/isBefore';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import startOfDay from 'date-fns/startOfDay';
import lscache from 'lscache';
import { Account } from 'model/IChat';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Logger from 'utils/Logger';
import { create } from 'zustand';

export type ExperienceFilterState = {
  nrOfAdults: number;
  nrOfChildren: number;
  selectedTime: string | null;
  selectedDate: string | null;
  selectedHost: Account | null;
  initializedForExperience: string | null;
};

type ExperienceFilterActions = {
  setInitials: (
    searchParamString?: string,
    maxPeople?: number,
    experienceId?: string,
  ) => void;
  setGuestsNumber: (params: {
    nrOfChildren: number;
    nrOfAdults: number;
  }) => void;
  setSelectedDate: (value: string) => void;
  setSelectedTime: (value: string) => void;
  setSelectedHost: (host: Account) => void;
  validateGuests: (maxPeople: number) => void;
};

type ExperienceFilterStore = ExperienceFilterState & {
  filterActions: ExperienceFilterActions;
};

function isBeforeToday(date: string) {
  return isBefore(
    startOfDay(parse(date, 'yyyy-MM-dd', new Date())),
    startOfDay(new Date()),
  );
}

function getInitialState(
  searchParamString?: string,
  maxPeople?: number,
): ExperienceFilterState {
  const searchParams = new URLSearchParams(searchParamString);
  const searchParamsAdults = searchParams.get('adults');
  const searchParamsChildren = searchParams.get('children');
  let nrOfAdults = searchParamsAdults
    ? parseInt(searchParamsAdults, 10)
    : (lscache.get('adults') ?? 4);
  let nrOfChildren = searchParamsChildren
    ? parseInt(searchParamsChildren, 10)
    : (lscache.get('children') ?? 0);

  if (maxPeople && nrOfAdults + nrOfChildren > maxPeople) {
    nrOfAdults = maxPeople;
    nrOfChildren = 0;
  }

  const dateParam = isValid(new Date(searchParams.get('date')))
    ? searchParams.get('date')
    : null;
  const searchDate = dateParam || lscache.get('date_from');
  const selectedDate =
    searchDate && isBeforeToday(searchDate) ? null : searchDate;

  const selectedTime = searchParams.get('time');

  return {
    nrOfAdults,
    nrOfChildren,
    selectedDate,
    selectedTime,
    selectedHost: null,
    initializedForExperience: null,
  };
}

function logger(type: string, value = '') {
  Logger.log(`ExperienceFilterStore: ${type} ${value ? `- ${value}` : ''}`);
}

const initialSearchParams =
  typeof window !== 'undefined' ? window.location.search : '';

const useExperienceFilterStoreBase = create<ExperienceFilterStore>()((set) => ({
  ...getInitialState(initialSearchParams),
  filterActions: {
    setInitials(searchParamString, maxPeople, experienceId) {
      const state = getInitialState(searchParamString, maxPeople);
      logger('SET_INITIALS', experienceId ? `experience: ${experienceId}` : '');
      set({
        ...state,
        initializedForExperience: experienceId ?? null,
      });
    },
    setGuestsNumber(params) {
      logger(
        'SET_GUESTS_NUMBER',
        `${params.nrOfAdults} adults, ${params.nrOfChildren} children`,
      );
      set(params);
    },
    setSelectedDate(value) {
      if (!value) return;
      logger('SET_SELECTED_DATE', value);
      set({ selectedDate: value });
    },
    setSelectedTime(value) {
      if (!value) return;
      logger('SET_SELECTED_TIME', value);
      set({ selectedTime: value });
    },
    setSelectedHost(host) {
      if (!host) return;
      logger('SET_SELECTED_HOST', host.id);
      set({ selectedHost: host });
    },
    validateGuests(maxPeople) {
      set((state) => {
        if (state.nrOfAdults + state.nrOfChildren > maxPeople) {
          logger('VALIDATE_GUESTS', `clamped to ${maxPeople}`);
          return { nrOfAdults: maxPeople, nrOfChildren: 0 };
        }
        return {};
      });
    },
  },
}));

export function useExperienceFilterStore(
  experienceId?: string,
  maxPeople?: number,
) {
  const store = useExperienceFilterStoreBase();
  const location = useLocation();

  useEffect(() => {
    // Only initialize when navigating to a different experience (or first load)
    if (experienceId && store.initializedForExperience !== experienceId) {
      store.filterActions.setInitials(location.search, maxPeople, experienceId);
    }
  }, [
    experienceId,
    store.initializedForExperience,
    store.filterActions,
    location.search,
    maxPeople,
  ]);

  useEffect(() => {
    if (maxPeople) {
      store.filterActions.validateGuests(maxPeople);
    }
  }, [maxPeople, store.filterActions]);

  return store;
}
